import React, { useState, useRef } from 'react';
import { lastDayOfMonth, subMonths } from 'date-fns';
import styled from '@emotion/styled';
import { Title, Content, DialogActions } from 'ui/views/dialogs/Dialog';
import KPIGraph from 'domain/kpis/KPIGraph';
import { KpiWithDisplay, IndicatorType, DisplayType, ComparisonPeriod } from 'domain/kpis/types';
import KpiAPI, { AddOrDeleteKpiValue } from 'apis/KpiAPI';
import useLazyResource from 'util/resource/useLazyResource';
import useNotify from 'hooks/useNotify';
import { asMonthNameYear } from 'util/dateUtils';
import MonthInputRow from './MonthInputRow';
import { useCompanyLastUpdated } from 'domain/companies/profile/LastUpdated';
import { contentSpacing, halfSpacing, sectionSpacing } from 'ui/theme/themeConstants';
import { bluePlanetTheme } from 'ui/theme';
import TextField from 'ui/elements/form/TextField';
import { Select, MenuItem, Checkbox, FormControlLabel } from '@mui/material';
import { useBreakpoints } from 'hooks/useBreakpoints';
import MaxDataPointsSlider from 'domain/kpis/MaxDataPointsSlider';
import Accordion from 'ui/modules/Accordion';
import Button from 'ui/elements/buttons/Button';
import ButtonList from 'ui/elements/buttons/ButtonList';
import Label from 'ui/elements/form/Label';
import Dialog from 'ui/views/dialogs/Dialog';
import { invalidate } from 'hooks/useSWR';

const ModalLayout = styled.div<{ $isMobile: boolean }>`
  display: flex;
  gap: ${sectionSpacing};

  ${bluePlanetTheme.breakpoints.down('sm')} {
    flex-direction: column;
    gap: ${contentSpacing};
  }
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const Sidebar = styled.div<{ $isMobile: boolean }>`
  width: ${props => (props.$isMobile ? '100%' : '18rem')};
  padding: ${props => (props.$isMobile ? 0 : contentSpacing)};
  display: flex;
  flex-direction: column;
  gap: ${contentSpacing};

  ${bluePlanetTheme.breakpoints.down('sm')} {
    width: 100%;
    padding: 0;
  }
`;

const ShowMoreButton = styled.button`
  width: 100%;
  padding: ${contentSpacing};
  margin: ${halfSpacing} 0;
  border: 1px dashed ${bluePlanetTheme.bluePlanetPalette.grey.medium};
  border-radius: ${bluePlanetTheme.shape.borderRadius}px;
  background-color: transparent;
  color: ${bluePlanetTheme.bluePlanetPalette.blue.main};
  cursor: pointer;
  transition: ${bluePlanetTheme.transitions.create(['all'], {
    duration: bluePlanetTheme.transitions.duration.short,
  })};
  font-family: ${bluePlanetTheme.typography.fontFamily};
  font-size: ${bluePlanetTheme.typography.body1.fontSize};
  font-weight: ${bluePlanetTheme.typography.fontWeightMedium};

  &:hover {
    background-color: ${bluePlanetTheme.bluePlanetPalette.grey.light};
    border-color: ${bluePlanetTheme.bluePlanetPalette.blue.main};
  }

  &:active {
    background-color: ${bluePlanetTheme.bluePlanetPalette.grey.medium};
  }
`;

const AxisDisplayContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${halfSpacing};
`;

const SettingsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${contentSpacing};
`;

const GraphWrapper = styled.div`
  max-width: 44rem;
  margin-bottom: ${sectionSpacing};
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${bluePlanetTheme.palette.divider};
  margin: ${contentSpacing} 0;
`;

interface KpiEditModalContentProps {
  kpi: KpiWithDisplay;
  companyId: number;
  onClose: () => void;
  onSuccess: () => void;
}

// Helper to initialize staged values from KPI values
const getInitialStagedValues = (kpi: KpiWithDisplay): Map<string, { value: string }> => {
  const initialValues = new Map();
  kpi.values.forEach(v => {
    const monthKey = lastDayOfMonth(new Date(v.timestamp)).toISOString();
    initialValues.set(monthKey, {
      value: v.value.toString(),
    });
  });
  return initialValues;
};

export default function KpiEditModalContent({ kpi, companyId, onClose, onSuccess }: KpiEditModalContentProps) {
  const notify = useNotify();
  const { setLastUpdated } = useCompanyLastUpdated(companyId);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { isMobile } = useBreakpoints();

  // Initialize state from KPI (this happens fresh on each mount)
  const [kpiName, setKpiName] = useState(kpi.details.name);
  const [kpiUnit, setKpiUnit] = useState(kpi.details.unitOfMeasure || '');
  const [kpiTrend, setKpiTrend] = useState<IndicatorType>(kpi.display.trendIndicator);
  const [showXAxis, setShowXAxis] = useState(kpi.display.showXAxis);
  const [showYAxis, setShowYAxis] = useState(kpi.display.showYAxis);
  const [displayType, setDisplayType] = useState<DisplayType>(kpi.display.displayType);
  const [comparisonEnabled, setComparisonEnabled] = useState(kpi.display.comparisonEnabled);
  const [comparisonPeriod, setComparisonPeriod] = useState<'Month' | 'Year'>(
    kpi.display.displayType === 'BarChart' ? 'Year' : kpi.display.comparisonPeriod,
  );
  const [visibility, setVisibility] = useState<'Public' | 'Private'>(kpi.display.visibility);
  const [maxDataPoints, setMaxDataPoints] = useState<number>(kpi.display.maxDataPoints || 12);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const isArchived = !!kpi.details.archivedAt;

  // Track the range of months to display
  const [monthsOffset, setMonthsOffset] = useState(1); // Offset from current month (1 = last month, 0 = current month)
  const [monthsCount, setMonthsCount] = useState(12); // Total number of months to show

  // Generate list of months based on current range
  const monthsList = Array.from({ length: monthsCount }, (_, i) => {
    return lastDayOfMonth(subMonths(new Date(), monthsOffset + i));
  });

  // Initialize staged values from existing KPI values
  const [stagedValues, setStagedValues] = useState<Map<string, { value: string }>>(() => getInitialStagedValues(kpi));
  const originalValuesRef = useRef<Map<string, { value: string }>>(getInitialStagedValues(kpi));

  const [saveValues] = useLazyResource(
    (updates: AddOrDeleteKpiValue[]) => KpiAPI.kpi.updateValues(companyId, updates),
    {
      onSuccess: () => {
        setLastUpdated(new Date());
        onSuccess(); // This triggers mutate() in parent to refresh the data
        // Invalidate KPI list cache so embedded KPIs show fresh data
        invalidate(KpiAPI.kpi.list(companyId));
      },
      onFailure: () => {
        notify('error', 'Failed to save KPI value');
      },
    },
  );

  // Save KPI metadata (name, unit)
  const [saveKpiMetadata] = useLazyResource(
    ({ name, unitOfMeasure }: { name: string; unitOfMeasure?: string }) =>
      KpiAPI.kpi.edit(companyId, kpi.details.id, { name, unitOfMeasure }),
    {
      onSuccess: () => {
        setLastUpdated(new Date());
        onSuccess(); // Refresh parent data
        notify('success', 'KPI updated');
      },
      onFailure: () => {
        notify('error', 'Failed to update KPI');
      },
    },
  );

  // Save trend indicator
  const [saveTrendIndicator] = useLazyResource(
    (trendIndicator: IndicatorType) => KpiAPI.display.update(companyId, kpi.display.id, { trendIndicator }),
    {
      onSuccess: () => {
        setLastUpdated(new Date());
        onSuccess(); // Refresh parent data
        notify('success', 'Trend indicator updated');
      },
      onFailure: () => {
        notify('error', 'Failed to update trend indicator');
      },
    },
  );

  // Save axis display settings
  const [saveAxisSettings] = useLazyResource(
    ({ showXAxis, showYAxis }: { showXAxis: boolean; showYAxis: boolean }) =>
      KpiAPI.display.update(companyId, kpi.display.id, { showXAxis, showYAxis }),
    {
      onSuccess: () => {
        setLastUpdated(new Date());
        onSuccess(); // Refresh parent data
        notify('success', 'Axis settings updated');
      },
      onFailure: () => {
        notify('error', 'Failed to update axis settings');
      },
    },
  );

  // Save display type
  const [saveDisplayType] = useLazyResource(
    (displayType: DisplayType) => KpiAPI.display.update(companyId, kpi.display.id, { displayType }),
    {
      onSuccess: () => {
        setLastUpdated(new Date());
        onSuccess(); // Refresh parent data
        notify('success', 'Display type updated');
      },
      onFailure: () => {
        notify('error', 'Failed to update display type');
      },
    },
  );

  // Save comparison settings
  const [saveComparisonSettings] = useLazyResource(
    ({ comparisonEnabled, comparisonPeriod }: { comparisonEnabled: boolean; comparisonPeriod: 'Month' | 'Year' }) =>
      KpiAPI.display.update(companyId, kpi.display.id, { comparisonEnabled, comparisonPeriod }),
    {
      onSuccess: () => {
        setLastUpdated(new Date());
        onSuccess(); // Refresh parent data
        notify('success', 'Comparison settings updated');
      },
      onFailure: () => {
        notify('error', 'Failed to update comparison settings');
      },
    },
  );

  // Save visibility
  const [saveDisplayUpdate] = useLazyResource(
    (updates: { visibility?: 'Public' | 'Private'; maxDataPoints?: number }) =>
      KpiAPI.display.update(companyId, kpi.display.id, updates),
    {
      onSuccess: () => {
        setLastUpdated(new Date());
        onSuccess(); // Refresh parent data
        notify('success', 'Settings updated');
      },
      onFailure: () => {
        notify('error', 'Failed to update settings');
      },
    },
  );

  // Archive/Unarchive KPI
  const [archiveKpi, isArchiving] = useLazyResource(
    () => {
      if (isArchived) {
        return KpiAPI.kpi.unArchive(companyId, kpi.details.id);
      } else {
        return KpiAPI.kpi.archive(companyId, kpi.details.id);
      }
    },
    {
      onSuccess: () => {
        setLastUpdated(new Date());
        notify('success', isArchived ? 'KPI unarchived' : 'KPI archived');
        onSuccess(); // Refresh parent data
        onClose();
      },
      onFailure: () => {
        notify('error', `Failed to ${isArchived ? 'unarchive' : 'archive'} KPI`);
      },
    },
  );

  const handleArchiveClick = () => {
    if (isArchived) {
      // Unarchive directly without confirmation
      archiveKpi({});
    } else {
      // Show confirmation for archiving
      setShowArchiveConfirm(true);
    }
  };

  const handleConfirmArchive = () => {
    setShowArchiveConfirm(false);
    archiveKpi({});
  };

  const saveValueInstantly = (timestamp: string, value: string) => {
    const updates: Array<AddOrDeleteKpiValue> = [];
    const numericValue = parseFloat(value);

    if (value !== '' && !isNaN(numericValue)) {
      // Add value (replaces both create and update)
      updates.push({
        add: {
          kpiId: kpi.details.id,
          value: numericValue,
          date: timestamp,
        },
      });
    } else if (value === '') {
      // Delete when cleared
      updates.push({
        delete: {
          kpiId: kpi.details.id,
          date: timestamp,
        },
      });
    }

    if (updates.length > 0) {
      saveValues(updates);
    }
  };

  const handleValueChange = (timestamp: string, value: string) => {
    const updatedValues = new Map(stagedValues);
    updatedValues.set(timestamp, { value });
    setStagedValues(updatedValues);
  };

  const handleValueBlur = (timestamp: string) => {
    const currentValue = stagedValues.get(timestamp)?.value || '';
    const originalValue = originalValuesRef.current.get(timestamp)?.value || '';

    // Only save if the value has changed
    if (currentValue !== originalValue) {
      saveValueInstantly(timestamp, currentValue);
      // Update the original value after save
      originalValuesRef.current.set(timestamp, { value: currentValue });
    }
  };

  const handleNameChange = (name: string) => {
    setKpiName(name);
    saveKpiMetadata({ name, unitOfMeasure: kpiUnit });
  };

  const handleUnitChange = (unitOfMeasure: string) => {
    setKpiUnit(unitOfMeasure);
    saveKpiMetadata({ name: kpiName, unitOfMeasure });
  };

  const handleTrendChange = (trendIndicator: IndicatorType) => {
    setKpiTrend(trendIndicator);
    saveTrendIndicator(trendIndicator);
  };

  const handleAxisChange = (axis: 'x' | 'y', value: boolean) => {
    if (axis === 'x') {
      setShowXAxis(value);
      saveAxisSettings({ showXAxis: value, showYAxis });
    } else {
      setShowYAxis(value);
      saveAxisSettings({ showXAxis, showYAxis: value });
    }
  };

  const handleDisplayTypeChange = (newDisplayType: DisplayType) => {
    setDisplayType(newDisplayType);
    saveDisplayType(newDisplayType);

    // If switching to BarChart and comparison is enabled, ensure period is Year
    if (newDisplayType === 'BarChart' && comparisonEnabled && comparisonPeriod !== 'Year') {
      setComparisonPeriod('Year');
      saveComparisonSettings({ comparisonEnabled, comparisonPeriod: 'Year' });
    }
  };

  const handleComparisonEnabledChange = (enabled: boolean) => {
    setComparisonEnabled(enabled);
    // For bar charts, always use Year comparison
    const period = displayType === 'BarChart' ? 'Year' : comparisonPeriod;
    setComparisonPeriod(period);
    saveComparisonSettings({ comparisonEnabled: enabled, comparisonPeriod: period });
  };

  const handleComparisonPeriodChange = (period: 'Month' | 'Year') => {
    setComparisonPeriod(period);
    saveComparisonSettings({ comparisonEnabled, comparisonPeriod: period });
  };

  const handleKeyDown = (e: React.KeyboardEvent, currentIndex: number) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        // Shift+Tab: navigate upwards
        const prevIndex = currentIndex - 1;
        if (prevIndex >= 0 && inputRefs.current[prevIndex]) {
          inputRefs.current[prevIndex]?.focus();
        }
      } else {
        // Tab: navigate downwards
        const nextIndex = currentIndex + 1;
        if (inputRefs.current[nextIndex]) {
          inputRefs.current[nextIndex]?.focus();
        }
      }
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = currentIndex + 1;
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex]?.focus();
      }
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = currentIndex - 1;
      if (prevIndex >= 0 && inputRefs.current[prevIndex]) {
        inputRefs.current[prevIndex]?.focus();
      }
    }
  };

  const getValue = (month: Date): number | undefined => {
    const monthKey = month.toISOString();
    const staged = stagedValues.get(monthKey);
    if (staged) {
      const numValue = parseFloat(staged.value);
      return isNaN(numValue) ? undefined : numValue;
    }
    return undefined;
  };

  // Convert staged values to graph format
  const graphValues = Array.from(stagedValues.entries())
    .map(([timestamp, data]) => {
      const numValue = parseFloat(data.value);
      return {
        timestamp,
        value: numValue,
      };
    })
    .filter(item => !isNaN(item.value))
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  const handleShowFutureMonths = () => {
    // Move offset back to show more future months
    const newOffset = Math.max(monthsOffset - 6, -12); // Don't go more than 12 months into the future
    setMonthsOffset(newOffset);
  };

  const handleShowEarlierMonths = () => {
    // Increase count to show more historical months
    setMonthsCount(prev => prev + 12);
  };

  const canShowFutureMonths = monthsOffset > -12;
  const isChartDisplay = displayType === 'LineChart' || displayType === 'BarChart';
  const isNumberIndicator = displayType === 'NumberIndicator';
  const isBarChart = displayType === 'BarChart';

  const displaySettingsContent = (
    <>
      <TextField
        label="KPI Name"
        value={kpiName}
        onChange={e => handleNameChange(e.target.value)}
        placeholder="Revenue"
      />

      <TextField
        label="Unit of Measure"
        value={kpiUnit}
        onChange={e => handleUnitChange(e.target.value)}
        placeholder="$, %, etc."
      />

      <div>
        <Label>Display Type</Label>
        <Select
          value={displayType}
          onChange={e => handleDisplayTypeChange(e.target.value as DisplayType)}
          fullWidth
          size="small"
        >
          <MenuItem value="NumberIndicator">Number</MenuItem>
          <MenuItem value="LineChart">Line Chart</MenuItem>
          <MenuItem value="BarChart">Bar Chart</MenuItem>
        </Select>
      </div>

      <div>
        <Label>Trend Indicator</Label>
        <Select
          value={kpiTrend}
          onChange={e => handleTrendChange(e.target.value as IndicatorType)}
          fullWidth
          size="small"
        >
          <MenuItem value="Up">Up is Good</MenuItem>
          <MenuItem value="Down">Down is Good</MenuItem>
        </Select>
      </div>

      {isNumberIndicator && (
        <>
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={comparisonEnabled}
                  onChange={e => handleComparisonEnabledChange(e.target.checked)}
                  size="small"
                />
              }
              label="Show Comparison"
            />
          </div>

          {comparisonEnabled && (
            <div>
              <Label>Comparison Period</Label>
              <Select
                value={comparisonPeriod}
                onChange={e => handleComparisonPeriodChange(e.target.value as ComparisonPeriod)}
                fullWidth
                size="small"
              >
                <MenuItem value="Month">Previous month</MenuItem>
                <MenuItem value="Year">Previous year</MenuItem>
              </Select>
            </div>
          )}
        </>
      )}

      {isChartDisplay && (
        <div>
          <Label>Axis Display</Label>
          <AxisDisplayContainer>
            <FormControlLabel
              control={
                <Checkbox checked={showXAxis} onChange={e => handleAxisChange('x', e.target.checked)} size="small" />
              }
              label="Show X-axis"
            />
            <FormControlLabel
              control={
                <Checkbox checked={showYAxis} onChange={e => handleAxisChange('y', e.target.checked)} size="small" />
              }
              label="Show Y-axis"
            />
          </AxisDisplayContainer>
        </div>
      )}

      <div>
        <Label>Visibility</Label>
        <Select
          value={visibility}
          onChange={e => {
            const newVisibility = e.target.value as 'Public' | 'Private';
            setVisibility(newVisibility);
            saveDisplayUpdate({ visibility: newVisibility });
          }}
          fullWidth
          size="small"
        >
          <MenuItem value="Private">Shareholders and board members</MenuItem>
          <MenuItem value="Public">Anyone</MenuItem>
        </Select>
      </div>

      {isBarChart && (
        <>
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={comparisonEnabled}
                  onChange={e => handleComparisonEnabledChange(e.target.checked)}
                  size="small"
                />
              }
              label="Show Year-over-Year Comparison"
            />
            {comparisonEnabled && (
              <p className="text-metadata u-color-grey-dark u-quarter-spacing-top">
                Previous year values shown as grey bars
              </p>
            )}
          </div>

          <MaxDataPointsSlider
            value={maxDataPoints}
            onChange={newValue => {
              setMaxDataPoints(newValue);
              saveDisplayUpdate({ maxDataPoints: newValue });
            }}
          />
        </>
      )}

      <Divider />
      <div>
        <Button kind="tertiary" onClick={handleArchiveClick} isLoading={isArchiving}>
          {isArchived ? 'Unarchive KPI' : 'Archive KPI'}
        </Button>
      </div>
    </>
  );

  return (
    <>
      <Title onClose={onClose}>Edit KPI</Title>
      <Content paddingY={isMobile ? 'none' : 'both'}>
        <ModalLayout $isMobile={isMobile}>
          {isMobile && (
            <Sidebar $isMobile={isMobile} className="u-content-spacing-bottom">
              <Accordion
                title="Display Settings"
                isExpanded={false}
                paddingContent="default"
                summaryBackgroundColor="grey-light"
              >
                <SettingsSection>{displaySettingsContent}</SettingsSection>
              </Accordion>
            </Sidebar>
          )}

          <MainContent>
            {!isMobile && (
              <GraphWrapper>
                <KPIGraph
                  name={kpiName}
                  unitOfMeasure={kpiUnit}
                  values={graphValues}
                  displayType={displayType}
                  trendIndicator={kpiTrend}
                  showXAxis={showXAxis}
                  showYAxis={showYAxis}
                  comparisonEnabled={comparisonEnabled}
                  comparisonPeriod={comparisonPeriod}
                  dataPointCount={displayType === 'BarChart' ? maxDataPoints : undefined}
                />
              </GraphWrapper>
            )}

            <div>
              {canShowFutureMonths && (
                <ShowMoreButton type="button" onClick={handleShowFutureMonths}>
                  + Show future months
                </ShowMoreButton>
              )}

              {monthsList.map((month, index) => (
                <MonthInputRow
                  key={month.toISOString()}
                  month={asMonthNameYear(month)}
                  value={getValue(month)}
                  unitOfMeasure={kpiUnit}
                  onChange={value => handleValueChange(month.toISOString(), value)}
                  onBlur={() => handleValueBlur(month.toISOString())}
                  onKeyDown={e => handleKeyDown(e, index)}
                  inputRef={ref => (inputRefs.current[index] = ref)}
                  autoFocus={index === 0 && monthsOffset === 1}
                />
              ))}

              <ShowMoreButton type="button" onClick={handleShowEarlierMonths}>
                + Show earlier months
              </ShowMoreButton>
            </div>
          </MainContent>

          {!isMobile && (
            <Sidebar $isMobile={isMobile}>
              <div>
                <h3 className="text-h4 u-half-spacing-bottom">Display Settings</h3>
                <p className="text-metadata u-color-grey-dark u-content-spacing-bottom">
                  Configure how this KPI appears
                </p>
              </div>
              {displaySettingsContent}
            </Sidebar>
          )}
        </ModalLayout>
      </Content>
      <DialogActions>
        <Button kind="tertiary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveConfirm} onClose={() => setShowArchiveConfirm(false)}>
        <Title onClose={() => setShowArchiveConfirm(false)}>Archive KPI?</Title>
        <Content>
          <p>
            Are you sure you want to archive <strong>{kpiName}</strong>? This will hide it from the overview, but you
            can unarchive it later.
          </p>
        </Content>
        <DialogActions>
          <ButtonList>
            <Button kind="primary" onClick={handleConfirmArchive} isLoading={isArchiving}>
              Archive
            </Button>
            <Button kind="tertiary" onClick={() => setShowArchiveConfirm(false)}>
              Cancel
            </Button>
          </ButtonList>
        </DialogActions>
      </Dialog>
    </>
  );
}
